import React, { useState, useEffect, useMemo, forwardRef } from "react";
import { useNavigate } from "react-router-dom";
import Loader from "react-spinner-loader";
import { userTableDatacolumnsForExport } from "../../../../JsonData/ContactConfig";
import {
  deleteUserById,
  getUsersFromAppByRole,
  getThemeURL,
} from "../../../../Services/EndPoints";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import {
  getData,
  postDataWithBody,
  putDataWithBody,
} from "../../../../Services/Services";
import { getExportedColumnsData } from "../../../../Components/UiComponents/utils";
import { setTableData } from "../../../../CentralStore/ExportLMSTable/dataSlice";
import Confirmpopup from "../../Common/ConfirmPopup/Confirmpopup";
import { useTranslation } from "react-i18next";
import {
  listStyles,
  formatDateTime,
  pill,
  ViewButton,
  EditButton,
  DeleteButton,
  ListPager,
} from "../../Common/ListCard/ListCard";

// ─── Row helpers ──────────────────────────────────────────────────────────────
const fullName = (u) => [u.first_name, u.last_name].filter(Boolean).join(" ");
const roleNames = (u) => u.user_role_map?.map((r) => r.roleEntity?.name).filter(Boolean).join(", ") || "";
const departmentName = (u) => u.user_department_map?.[0]?.departmentEntity?.name || "";
// Component for displaying and managing users (bank Manage User list)
const UserTable = forwardRef(() => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [userData, setUserData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [deleteUser, setDeleteUser] = useState(null); // row awaiting delete confirmation

  // Fetches user profile data on component mount
  useEffect(() => {
    getUserProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetches user theme profile and triggers user list retrieval
  const getUserProfile = async () => {
    try {
      const userProfile = await getData(getThemeURL);
      if (userProfile && userProfile.length > 0) {
        getUserList(userProfile[0]);
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
      toast.error("Failed to fetch user profile", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    }
  };

  // Creates API payload based on user profile role and organization details
  const createPayload = (profile) => {
    if (profile.role_name === "SUPER ADMIN") {
      return {
        admin_role: "SA",
        bank_id: profile.bank_id || null,
        branch_id: profile.branch_id || null,
      };
    } else if (profile.is_admin === true) {
      return {
        admin_role: "HO",
        bank_id: profile.bank_id || null,
        userId: profile.id,
      };
    } else {
      return {
        admin_role: "U",
        bank_id: profile.bank_id || null,
        branch_id: profile.branch_id || null,
      };
    }
  };

  // Fetches the user list for the logged-in user's role/bank
  const getUserList = async (profile) => {
    setLoader(true);
    const userList = await postDataWithBody(getUsersFromAppByRole, createPayload(profile), {});
    setLoader(false);
    setUserData(Array.isArray(userList) ? userList : []);
  };

  // ── Search + pagination ──
  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return userData;
    return userData.filter((u) =>
      fullName(u).toLowerCase().includes(q) ||
      u.user_name?.toLowerCase().includes(q) ||
      u.phone_number?.includes(q) ||
      departmentName(u).toLowerCase().includes(q) ||
      roleNames(u).toLowerCase().includes(q) ||
      (u.userLevel || "").toLowerCase().includes(q)
    );
  }, [userData, searchText]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const pageRows = filtered.slice(startIndex, startIndex + pageSize);

  useEffect(() => { setPage(1); }, [searchText, pageSize]);

  // Keep the header "Download" export in sync with what's searched
  useEffect(() => {
    sendDataToCentralStore(filtered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered]);

  // Processes and dispatches user data to Redux store for export functionality
  const sendDataToCentralStore = (data) => {
    if (data.length > 0) {
      const columns = userTableDatacolumnsForExport;
      const processedData = data.map((item) => ({
        ...item,
        user_role_map: roleNames(item),
        full_name: fullName(item),
        department: departmentName(item) || "NA",
        createdOn: item.createdOn
          ? new Date(item.createdOn).toLocaleDateString("en-GB") +
            ", " +
            new Date(item.createdOn).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
          : "NA",
      }));
      dispatch(
        setTableData({
          columns,
          tableData: getExportedColumnsData(columns, processedData),
        })
      );
    }
  };

  // ── Actions ──
  const addNewHandler = () => navigate("/users-management/create-user");

  const viewUser = (u) => navigate("/users-management/user-view-det", { state: { data: u } });

  const editUser = (u) =>
    navigate("/users-management/create-user", {
      state: { data: u, isBankFieldDisable: true, edit: true }, // bank stays locked when editing (as before)
    });

  // Deletes user by ID and refreshes the user list
  const deleteTableDataById = async (id) => {
    const res = await putDataWithBody(deleteUserById, { id }, {});
    if (res?.status === "success") {
      getUserProfile();
      toast.success(res.msg, { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
    } else {
      toast.error(res?.msg || "Failed to delete user", { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
    }
    setDeleteUser(null);
  };

  const headers = ["Name", "Username", "Phone", "Created On", "Department", "User Level", "Role", "Status", "Actions"];

  return (
    <div style={listStyles.page}>
      <Loader show={loader} type="body" stack="vertical" message="Loading Data" />

      {/* Top bar */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
        <button type="button" onClick={addNewHandler} style={listStyles.primaryBtn}>
          + {t("createuseraddbuttonlabel")}
        </button>
        <input
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search by name, username, phone, department, user level, role..."
          title="Search by Name, Username, Phone, Department, User Level or Role"
          style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 12, width: 280, maxWidth: "100%", outline: "none" }}
        />
      </div>

      <div style={listStyles.card}>

        {/* Table */}
        <div style={{ overflowX: "auto" }}>
          {!loader && filtered.length === 0 ? (
            <div style={listStyles.empty}>No users found.</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "linear-gradient(135deg, #0f4a45, #1a7a73)" }}>
                  {headers.map((h) => <th key={h} style={{ ...listStyles.th, color: "#fff" }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((u, i) => {
                  const active = u.status !== false;
                  const name = fullName(u);
                  return (
                    <tr
                      key={u.id || i}
                      style={{ borderBottom: "1px solid #f1f5f9" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <td style={{ ...listStyles.td, color: "#1e293b", fontWeight: 600, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={name}>
                        {name || "—"}
                      </td>
                      <td style={listStyles.td}>
                        <span style={listStyles.tag}>{u.user_name || "—"}</span>
                      </td>
                      <td style={listStyles.td}>{u.phone_number || "—"}</td>
                      <td style={{ ...listStyles.td, whiteSpace: "nowrap" }}>{formatDateTime(u.createdOn) || "—"}</td>
                      <td style={listStyles.td}>{departmentName(u) || "—"}</td>
                      <td style={listStyles.td}>{u.userLevel || "—"}</td>
                      <td style={listStyles.td}>{roleNames(u) || "—"}</td>
                      <td style={listStyles.td}>
                        {pill(active, "Active", "Inactive")}
                      </td>
                      <td style={listStyles.td}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <ViewButton onClick={() => viewUser(u)} />
                          <EditButton onClick={() => editUser(u)} />
                          <DeleteButton onClick={() => setDeleteUser(u)} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <ListPager
          total={filtered.length}
          page={currentPage}
          pageSize={pageSize}
          onPage={setPage}
          onPageSize={setPageSize}
        />
      </div>

      {/* Delete confirmation (same popup as before) */}
      {deleteUser && (
        <Confirmpopup
          className="DeltePopup"
          show={!!deleteUser}
          title={t("deleteuserpopuptext")}
          message={t("deleteuserpopupmessage")}
          handleToggle={() => setDeleteUser(null)}
          row={{ original: deleteUser }}
          name={fullName(deleteUser)}
          deleteRow={deleteTableDataById}
          cancel={t("cancelbutton")}
          action={t("deactivatebutton")}
        />
      )}
    </div>
  );
});

export default UserTable;
